import {
  MEETING_ROOM_COVER_FILE_TYPE,
  MEETING_ROOM_SLIDER_FILE_TYPE,
} from './constants'
import {
  mapListResponse,
  MeetingRoomFileDtoLike,
  normalizeMeetingRoomFiles,
  partitionMeetingRoomImageFiles,
  readApiField,
  readEquipmentNames,
} from './apiMappers'
import { parseMeetingRoomPurposeCode } from 'domain/feature/hub/company/meeting-room/manage-meeting-room/api/constants'
import { IPublicMeetingRoom, IPublicMeetingRoomGalleryImage } from '../types'

const toGalleryImage = (file: MeetingRoomFileDtoLike): IPublicMeetingRoomGalleryImage => ({
  url: file.path,
  alt: file.originalName?.trim() ?? '',
})

const buildGalleryImages = (
  coverImage: IPublicMeetingRoomGalleryImage | null,
  sliderImages: IPublicMeetingRoomGalleryImage[]
): IPublicMeetingRoomGalleryImage[] => {
  const images: IPublicMeetingRoomGalleryImage[] = []
  const coverUrl = coverImage?.url.trim() ?? ''

  if (coverUrl !== '') {
    images.push({
      url: coverUrl,
      alt: coverImage?.alt ?? '',
    })
  }

  sliderImages.forEach(sliderImage => {
    const sliderUrl = sliderImage.url.trim()

    if (sliderUrl !== '' && sliderUrl !== coverUrl) {
      images.push(sliderImage)
    }
  })

  return images
}

const mapPublicMeetingRoomDto = (item: unknown): IPublicMeetingRoom | null => {
  if (item == null || typeof item !== 'object') {
    return null
  }

  const row = item as Record<string, unknown>
  const id = readApiField<string>(row, 'id', 'Id')
  const name = readApiField<string>(row, 'name', 'Name')?.trim()

  if (id == null || id === '' || name == null || name === '') {
    return null
  }

  const files = normalizeMeetingRoomFiles(readApiField<unknown[]>(row, 'files', 'Files'))
  const { cover, sliders } = partitionMeetingRoomImageFiles(
    files,
    MEETING_ROOM_COVER_FILE_TYPE,
    MEETING_ROOM_SLIDER_FILE_TYPE
  )

  const coverImage = cover != null ? toGalleryImage(cover) : null
  const sliderImages = sliders.map(toGalleryImage)
  const galleryImages = buildGalleryImages(coverImage, sliderImages)

  const maximumCapacity = readApiField<number>(row, 'maximumCapacity', 'MaximumCapacity')
  const chairQuantity = readApiField<number>(row, 'chairQuantity', 'ChairQuantity')
  const description = readApiField<string>(row, 'description', 'Description')?.trim() ?? ''
  const purposeRaw = readApiField<string | number>(row, 'purpose', 'Purpose')

  return {
    id: String(id),
    name,
    description,
    maxCapacity: maximumCapacity ?? chairQuantity ?? 0,
    purposeCode: parseMeetingRoomPurposeCode(purposeRaw),
    imageUrl: coverImage?.url ?? sliderImages[0]?.url ?? '',
    sliderImages,
    galleryImages,
    equipment: readEquipmentNames(row),
  }
}

export const normalizePublicMeetingRoomListResponse = (response: unknown): IPublicMeetingRoom[] =>
  mapListResponse(response, mapPublicMeetingRoomDto)
